import { useMemo, useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { axiosInstance } from 'src/api/axios';
import APIEndpoints from 'src/Member/api/APIEndpoints';
import * as yup from 'yup';
import { UNLOCK_ACCOUNT_FIELD_CONFIG } from 'src/constants';
import { useSelector } from 'react-redux';
import moment from 'moment';

const useAccountUnlockRecovery = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [buttonDisable, setButtonDisable] = useState(false);
  const headingRef = useRef(null);

  const email = location?.state?.email || '';
  const { adminConfigData } = useSelector((state) => state.user);
  const resetOptions = adminConfigData?.Response?.ResetOptions || [];
  const fieldData =
    resetOptions.find((item) => item.RecordType === 'SAUnlock') || {};

  const visibleFields = useMemo(() => {
    return Object.entries(fieldData)
      .filter(([_, value]) => ['Visible', 'InVisible'].includes(value))
      .filter(([key]) => UNLOCK_ACCOUNT_FIELD_CONFIG[key])
      .map(([key, value]) => ({
        ...UNLOCK_ACCOUNT_FIELD_CONFIG[key],
        visibility: value,
      }));
  }, [fieldData]);

  const initialValues = useMemo(() => {
    return visibleFields.reduce((acc, field) => {
      acc[field.name] = null;
      return acc;
    }, {});
  }, [visibleFields]);

  const validationSchema = useMemo(() => {
    return yup.object(
      visibleFields.reduce((acc, field) => {
        if (field.type === 'date') {
          acc[field.name] =
            field.visibility === 'Visible'
              ? yup.date().required(`${field.label} is required`)
              : yup.date().nullable();
        } else {
          acc[field.name] =
            field.visibility === 'Visible'
              ? yup.string().required(`${field.label} is required`)
              : yup.string().nullable();
        }

        return acc;
      }, {}),
    );
  }, [visibleFields]);

  const verifyUserAccount = async (values) => {
    if (!fieldData) return;

    const payloadFieldMap = {
      DateOfBirth: 'BirthDate',
    };

    const fieldNameMap = {
      FirstName: 'firstName',
      LastName: 'lastName',
      DateOfBirth: 'dateOfBirth',
      Email: 'email',
      PhoneNumber: 'phoneNumber',
      SocialSecurityNumber: 'ssn',
      CertificateNumber: 'certificateNumber',
      PostalCode: 'postalCode',
      Relationship: 'relationship',
      AlternateID1: 'alternateId1',
      AlternateID2: 'alternateId2',
      MedicareID: 'medicareId',
    };
    const requiredFields = Object.entries(fieldData)
      .filter(([_, value]) => value === 'Visible')
      .map(([key]) => payloadFieldMap[key] || key);
    setButtonDisable(true);

    try {
      const payload = {
        UserID: email,
        NumberOfItemsToMatch: requiredFields.length,
        RequiredFields: requiredFields,
      };
      Object.keys(fieldData).forEach((key) => {
        if (['Visible', 'InVisible'].includes(fieldData[key])) {
          const formikField = fieldNameMap[key];

          if (formikField && values[formikField] !== undefined) {
            const payloadKey = payloadFieldMap[key] || key;

            payload[payloadKey] =
              key === 'DateOfBirth'
                ? moment(values[formikField]).format('YYYY-MM-DD')
                : values[formikField];
          }
        }
      });
      const response = await axiosInstance.post(
        `${APIEndpoints.postSelfAccountVerification()}`,
        payload,
      );
      navigate('/member/account-unlock-response', {
        state: {
          status: 'success',
        },
      });
    } catch (e) {
      navigate('/member/account-unlock-response', {
        state: {
          status: 'fail',
        },
      });
      console.log(e);
    } finally {
      setButtonDisable(false);
    }
  };

  useEffect(() => {
    if (!email) {
      navigate('/member/');
    }
  }, [location, navigate]);
  useEffect(() => {
    if (headingRef.current) {
      headingRef.current.focus();
    }
  }, []);

  return {
    email,
    visibleFields,
    initialValues,
    validationSchema,
    verifyUserAccount,
    buttonDisable,
    navigate,
    headingRef,
  };
};

export default useAccountUnlockRecovery;
